import { Body, H2, H3 } from '@casavo/habitat'
import useTranslation from 'next-translate/useTranslation'
import getConfig from 'next/config'
import Image from 'next/image'
import React, { Dispatch, SetStateAction } from 'react'

import { ConceptImage } from '@/components/ConceptImage'
import { Translated } from '@/components/Translated'
import { useAuthentication } from '@/context/AuthProvider'
import { useCurrentTenant } from '@/context/TenantProvider'
import { Button } from '@/design-lib/components/Button'
import { sprinkles } from '@/design-lib/style-theme/sprinkles.css'
import { TENANTS } from '@/lib/tenants/constants'
import { Tenants } from '@/lib/tenants/types'
import { formatListingSellingPrice } from '@/utils/format-listing-sellingPrice'
import { GTMEventCategory } from '@/utils/tracking/eventCategory'
import { handleClick } from '@/utils/tracking/eventsHandlers'

import { RequestVisitCTA } from '../../RequestVisit/RequestVisitCTA'

type Props = {
  disableVisitCta: boolean
  handleRequestVisitCtaBuyerInfo: VoidFunction
  hasBuyerInfo: boolean
  isSuccess: boolean
  price: {
    askedPrice: { cents: number }
    discountedPrice: number
    estimatedListingPrice: string
  }
  setShowToastError: Dispatch<SetStateAction<boolean>>
  showAlreadyBookedBanner: boolean
  thankYouPageUrl: string
  transactionId: string
}

const tenantTitleTypography: Record<Tenants, typeof H2 | typeof H3> = {
  [TENANTS.casavo]: H2,
  [TENANTS.ucsc]: H3,
}

export const ModalPrice: React.FC<Props> = ({
  disableVisitCta,
  handleRequestVisitCtaBuyerInfo,
  hasBuyerInfo,
  isSuccess,
  price,
  setShowToastError,
  showAlreadyBookedBanner,
  thankYouPageUrl,
  transactionId,
}) => {
  const tenant = useCurrentTenant()
  const { lang, t } = useTranslation()
  const { user } = useAuthentication()

  const TitleTypography = tenantTitleTypography[tenant]

  const title = isSuccess
    ? t('priority:PriceAnalysis_GoodPrice_Modal_Title')
    : t('priority:PriceAnalysis_HighPrice_Modal_Title')
  const body = isSuccess ? 'priority:PriceAnalysis_GoodPrice_Modal_Body' : 'priority:PriceAnalysis_HighPrice_Modal_Body'
  const imageName = isSuccess ? 'ideas' : 'salary'
  const fsProperty = isSuccess ? 'GoodPrice' : 'HighPrice'
  const requestVisitCtaHandleClick = () =>
    handleClick(GTMEventCategory.PriorityDetail, 'PriceAnalysis_ModalCTA', fsProperty)

  return (
    <>
      <div className={sprinkles({ marginBottom: '2xl' })}>
        <div className={sprinkles({ marginBottom: 'm', textAlign: 'center' })}>
          {tenant === TENANTS.casavo && <ConceptImage height={106} name={imageName} width={106} />}
          {tenant === TENANTS.ucsc && <UCSCAmount />}
        </div>

        <div style={{ textAlign: 'center' }}>
          <TitleTypography>{title}</TitleTypography>
        </div>
        <Body size="m">
          <Translated
            i18Key={body}
            values={{
              AskingPrice: formatListingSellingPrice(false, price.askedPrice, t, lang),
              Discount: price.discountedPrice,
              SuggestedPrice: price.estimatedListingPrice,
            }}
          />
        </Body>
      </div>
      {!showAlreadyBookedBanner &&
        (hasBuyerInfo ? (
          <Button
            fullWidth
            data-testid="FromPriceAnalysis"
            disabled={disableVisitCta}
            label={t('priority:Priority_DetailPage_FloatingBanner_CTA')}
            loading={user && disableVisitCta}
            minWidth={200}
            styleVariant="primary"
            onClick={handleRequestVisitCtaBuyerInfo}
          />
        ) : (
          <RequestVisitCTA
            hasFullLabelOnMobile
            className={sprinkles({ margin: '0 auto' })}
            gtmTouchPoint="FromPriceAnalysis"
            handleCtaClick={requestVisitCtaHandleClick}
            isFullWidth={false}
            setShowToastError={setShowToastError}
            thankYouPageUrl={thankYouPageUrl}
            transactionId={transactionId}
          />
        ))}
    </>
  )
}

const UCSCAmount: React.FC = () => {
  const {
    publicRuntimeConfig: { imageBasePath },
  } = getConfig()

  return <Image alt={'amount'} height={48} src={`${imageBasePath}/images/tenants/ucsc/amount.svg`} width={48} />
}
